import { SIXES_BUCKETS, type SixesBucket } from "./sixesMarketBucket";
import { isSixesCellWon, type SixesCellOutcome } from "./sixesLiveData";
import {
    marketCellGridPosition,
    SIXES_MARKET_CELL_COUNT,
    type SixesGridCellSlot,
} from "./sixesGridLayout";
import {
    applySixesSwaps,
    isBucketSlideReady,
    mergedRunsWonCellIndices,
    mergedPlayerWonCellIndices,
    SIXES_LINE_TARGETS,
    SIXES_MERGED_RUNS_SLIDE_BUCKET,
    SIXES_MERGED_PLAYER_SLIDE_BUCKET,
    bothRunsBucketsOnCard,
    bothPlayerBucketsOnCard,
    type SixesClaimKind,
    type SixesLineId,
    type SixesMovementPlan,
} from "./sixesMovement";
import { countSixesSets } from "./sixesSetCount";
import { countSixesPrizeCategories } from "./sixesPrizeCounts";
import type { GeneratedSixesDemoCard } from "../demo/sixesDemoCard";
import type { SixesSeenFooterStats } from "./sixesSettlementSeenStorage";
import type { SixesFooterStatVariant } from "../ui/SixesFooterStatValue";
import { SIXES_BUCKET_SLIDE_MIN_WINS, type SixesSlideDelta } from "./sixesBoardConstants";

/**
 * Bucket-completion, slide-celebration, settlement and footer-stat logic for the Sixes
 * match-card play screen. Pure functions (no component state) shared by
 * {@link SixesMatchCardView}.
 */

export type SixesBucketMap = readonly (SixesBucket | null)[];

export type FooterJuiceKey = SixesFooterStatVariant;

export const FOOTER_JUICE_ORDER = ["wins", "slides", "natural", "multiplier", "coins"] as const;

export function sortFooterJuiceKeys(keys: readonly FooterJuiceKey[]): FooterJuiceKey[] {
    const set = new Set(keys);
    return FOOTER_JUICE_ORDER.filter((key) => set.has(key));
}

export function footerStatsIncreased(prev: SixesSeenFooterStats, next: SixesSeenFooterStats): FooterJuiceKey[] {
    const juice: FooterJuiceKey[] = [];
    if (next.wins > prev.wins) juice.push("wins");
    if (next.slides > prev.slides) juice.push("slides");
    if (next.natural > prev.natural) juice.push("natural");
    if (next.multiplierSixes > prev.multiplierSixes) juice.push("multiplier");
    if (next.coins > prev.coins) juice.push("coins");
    return sortFooterJuiceKeys(juice);
}

export function isCellWon(outcome: SixesCellOutcome): boolean {
    return isSixesCellWon(outcome);
}

export function bucketsMapFromMarketCells(cells: readonly SixesGridCellSlot[]): SixesBucketMap {
    return cells.map((c) => c.bucket);
}

export function uniformBucketOnLine(line: readonly number[], buckets: SixesBucketMap): SixesBucket | null {
    let bucket: SixesBucket | null = null;
    for (const idx of line) {
        const b = buckets[idx];
        if (!b) return null;
        if (bucket == null) bucket = b;
        else if (bucket !== b) return null;
    }
    return bucket;
}

/** Market-cell indices that belong to a bucket, for the current (generated) card. */
export function bucketCellIndices(buckets: SixesBucketMap, bucket: SixesBucket): number[] {
    const out: number[] = [];
    buckets.forEach((b, i) => {
        if (b === bucket) out.push(i);
    });
    return out;
}

export function bucketWonCellIndices(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    bucket: SixesBucket,
): number[] {
    if (bothRunsBucketsOnCard(buckets) && bucket === SIXES_MERGED_RUNS_SLIDE_BUCKET) {
        return mergedRunsWonCellIndices(buckets, outcomes);
    }
    if (bothPlayerBucketsOnCard(buckets) && bucket === SIXES_MERGED_PLAYER_SLIDE_BUCKET) {
        return mergedPlayerWonCellIndices(buckets, outcomes);
    }
    return bucketCellIndices(buckets, bucket).filter((idx) => isCellWon(outcomes[idx] ?? "pending"));
}

export function bucketWonCount(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    bucket: SixesBucket,
): number {
    return bucketWonCellIndices(buckets, outcomes, bucket).length;
}

export { isBucketSlideReady };

/** Buckets to mark celebrated/applied when a slide animation runs (merged runs → both sides). */
export function slideCelebrationBuckets(buckets: SixesBucketMap, bucket: SixesBucket): SixesBucket[] {
    if (bothRunsBucketsOnCard(buckets) && bucket === SIXES_MERGED_RUNS_SLIDE_BUCKET) {
        return ["team_a_runs", "team_b_runs"];
    }
    if (bothPlayerBucketsOnCard(buckets) && bucket === SIXES_MERGED_PLAYER_SLIDE_BUCKET) {
        return ["team_a_player", "team_b_player"];
    }
    return [bucket];
}

export function findNewlySlideReadyBuckets(
    buckets: SixesBucketMap,
    prev: readonly SixesCellOutcome[],
    next: readonly SixesCellOutcome[],
    celebrated: ReadonlySet<SixesBucket>,
): SixesBucket[] {
    return SIXES_BUCKETS.filter(
        (b) =>
            !celebrated.has(b) &&
            !isBucketSlideReady(buckets, prev, b) &&
            isBucketSlideReady(buckets, next, b),
    );
}

/**
 * Buckets that are slide-ready right now and have not yet been animated/snapped this session.
 *
 * Unlike {@link findNewlySlideReadyBuckets}, this does NOT require the bucket to have *just* crossed
 * the threshold between two snapshots — a set is built whenever all of a bucket's cells are won (per
 * the slide threshold), regardless of the order or which poll each cell settled in. The
 * celebrated / applied / pending sets are the dedup, so a bucket animates exactly once.
 */
export function findUnshownSlideReadyBuckets(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    celebrated: ReadonlySet<SixesBucket>,
    layoutApplied: ReadonlySet<SixesBucket>,
    pendingAnimation: ReadonlySet<SixesBucket>,
): SixesBucket[] {
    return SIXES_BUCKETS.filter(
        (b) =>
            !celebrated.has(b) &&
            !layoutApplied.has(b) &&
            !pendingAnimation.has(b) &&
            isBucketSlideReady(buckets, outcomes, b),
    );
}

export function bucketWonIndicesOverlapCelebratedLine(
    wonIndices: readonly number[],
    skipIfLineCelebrated: readonly number[][],
): boolean {
    return skipIfLineCelebrated.some(
        (line) => line.length === wonIndices.length && wonIndices.every((idx) => line.includes(idx)),
    );
}

/** Buckets whose slide layout should be restored (slide threshold met, not yet applied this session). */
export function bucketsNeedingPersistedSlideRestore(
    bucketsMap: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    celebratedBuckets: ReadonlySet<SixesBucket>,
    layoutApplied: ReadonlySet<SixesBucket>,
    pendingAnimation: ReadonlySet<SixesBucket>,
    skipIfLineCelebrated: readonly number[][],
    restoreAllCompleted: boolean,
): SixesBucket[] {
    return SIXES_BUCKETS.filter((bucket) => {
        if (layoutApplied.has(bucket)) return false;
        if (pendingAnimation.has(bucket)) return false;
        if (!restoreAllCompleted && !celebratedBuckets.has(bucket)) return false;
        if (!isBucketSlideReady(bucketsMap, outcomes, bucket)) return false;
        const wonIndices = bucketWonCellIndices(bucketsMap, outcomes, bucket);
        if (bucketWonIndicesOverlapCelebratedLine(wonIndices, skipIfLineCelebrated)) {
            return false;
        }
        return true;
    });
}

export function bucketPendingSiblingIndices(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    wonCellIndex: number,
): number[] {
    const bucket = buckets[wonCellIndex];
    if (!bucket) return [];
    return bucketCellIndices(buckets, bucket).filter((idx) => {
        if (idx === wonCellIndex) return false;
        return (outcomes[idx] ?? "pending") === "pending";
    });
}

/**
 * Travel from one grid slot to another, in whole tracks. Deliberately resolution-independent: the
 * board's track size changes with the viewport, so offsets are turned into pixels by the browser at
 * paint time (see {@link sixesSlideTransform}) instead of being measured off the grid mid-slide.
 */
export function slideSwapDelta(fromIdx: number, toIdx: number): SixesSlideDelta {
    const from = marketCellGridPosition(fromIdx);
    const to = marketCellGridPosition(toIdx);
    return { dCol: to.col - from.col, dRow: to.row - from.row };
}

/** Apply swap pairs onto a running offset map (same math as the animated slide celebration). */
export function accumulateSlideOffsetsFromSwaps(
    swaps: readonly { from: number; to: number }[],
    baseOffsets: Record<number, SixesSlideDelta>,
): { offsets: Record<number, SixesSlideDelta>; movers: number[] } {
    const offsets = { ...baseOffsets };
    const movers: number[] = [];
    for (const { from, to } of swaps) {
        offsets[from] = slideSwapDelta(from, to);
        if (!movers.includes(from)) movers.push(from);
    }
    return { offsets, movers };
}

/**
 * Absolute-from-home offsets for the *net permutation* of a composed movement plan.
 *
 * A plan's swaps form a single conflict-free permutation of slots. Replaying them on an identity
 * array yields `order[slot] = the home cell that ends up at that slot`; each home cell then gets one
 * offset (home → its final slot). This composes correctly across any number of buckets — unlike
 * per-bucket bidirectional swaps, where a slot that is both a mover and a displaced occupant would
 * clobber itself (last write wins) and break the line.
 */
export function offsetsFromPlanPermutation(
    planSwaps: readonly { from: number; to: number }[],
): { offsets: Record<number, SixesSlideDelta>; movers: number[]; order: number[] } {
    const identity = Array.from({ length: SIXES_MARKET_CELL_COUNT }, (_, i) => i);
    const order = applySixesSwaps(identity, planSwaps); // order[slot] = home cell now at slot
    const offsets: Record<number, SixesSlideDelta> = {};
    const movers: number[] = [];
    order.forEach((home, slot) => {
        if (home === slot) return;
        offsets[home] = slideSwapDelta(home, slot);
        movers.push(home);
    });
    return { offsets, movers, order };
}

const SIXES_VALID_LINE_IDS = new Set<string>(Object.keys(SIXES_LINE_TARGETS));
const SIXES_VALID_CLAIM_KINDS = new Set<string>(["four_draw", "six_draw_row", "six_draw_col"]);

/**
 * Validate + type the server-persisted `line_claims` JSON into a `SixesMovementPlan`. Returns null
 * for anything malformed so the caller can fall back to a local (server-consistent) recompute.
 */
export function parseServerMovementPlan(raw: unknown): SixesMovementPlan | null {
    if (!raw || typeof raw !== "object") return null;
    const obj = raw as { claims?: unknown; jackpot?: unknown };
    if (!Array.isArray(obj.claims)) return null;
    const claims: SixesMovementPlan["claims"] = [];
    for (const entry of obj.claims) {
        if (!entry || typeof entry !== "object") return null;
        const c = entry as Record<string, unknown>;
        if (typeof c.bucket !== "string") return null;
        if (typeof c.lineId !== "string" || !SIXES_VALID_LINE_IDS.has(c.lineId)) return null;
        if (typeof c.kind !== "string" || !SIXES_VALID_CLAIM_KINDS.has(c.kind)) return null;
        if (typeof c.rank !== "number") return null;
        if (!Array.isArray(c.swaps)) return null;
        const swaps: { from: number; to: number }[] = [];
        for (const s of c.swaps) {
            if (!s || typeof s !== "object") return null;
            const sw = s as Record<string, unknown>;
            if (typeof sw.from !== "number" || typeof sw.to !== "number") return null;
            swaps.push({ from: sw.from, to: sw.to });
        }
        claims.push({
            bucket: c.bucket as SixesBucket,
            lineId: c.lineId as SixesLineId,
            kind: c.kind as SixesClaimKind,
            rank: c.rank,
            swaps,
        });
    }
    return { claims, jackpot: Boolean(obj.jackpot) };
}

export function promoteBucketToGreen(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
    bucket: SixesBucket,
): SixesCellOutcome[] {
    let next = [...outcomes];
    const bucketsToPromote: SixesBucket[] =
        bothRunsBucketsOnCard(buckets) && bucket === SIXES_MERGED_RUNS_SLIDE_BUCKET
            ? ["team_a_runs", "team_b_runs"]
            : bothPlayerBucketsOnCard(buckets) && bucket === SIXES_MERGED_PLAYER_SLIDE_BUCKET
              ? ["team_a_player", "team_b_player"]
              : [bucket];
    for (const b of bucketsToPromote) {
        for (const idx of bucketCellIndices(buckets, b)) {
            if (next[idx] === "win-pink") next[idx] = "win-green";
        }
    }
    return next;
}

export function promoteAllCompletedBuckets(buckets: SixesBucketMap, outcomes: readonly SixesCellOutcome[]): SixesCellOutcome[] {
    let next = [...outcomes];
    for (const b of SIXES_BUCKETS) {
        if (isBucketSlideReady(buckets, next, b)) next = promoteBucketToGreen(buckets, next, b);
    }
    return next;
}

/** Fully-settled outcomes for a generated card (wins promoted to green where a bucket completes). */
export function buildSettledCellOutcomes(card: GeneratedSixesDemoCard): SixesCellOutcome[] {
    const resolved: SixesCellOutcome[] = card.buckets.map(() => "lost");
    for (const idx of card.winningIndices) resolved[idx] = "win-pink";
    return promoteAllCompletedBuckets(card.buckets, resolved);
}

export function settledFooterStats(card: GeneratedSixesDemoCard): {
    wins: number;
    coins: number;
    slides: number;
    natural: number;
} {
    const outcomes = buildSettledCellOutcomes(card);
    const { slidesCount, naturalCount } = countSixesPrizeCategories(card.buckets, outcomes);
    return {
        wins: card.winningIndices.length,
        coins: card.coinPayouts.reduce((sum, n) => sum + n, 0),
        slides: slidesCount,
        natural: naturalCount,
    };
}
